import { useState } from "react";
import Image from "next/image";

import logo from "../../../public/zeipt.png";

import { Button } from "@/components/common";

import { Receipt, ExtendedViewItem } from "@/types/receipt";
import { ExtendedServicePayload } from "@/types/extendedViewService";

import { translate, formatDate } from "@/utils/formatters";
import { getReceiptTypeDisplayText } from "@/utils/receiptType";
import { useExtendedServicePopup } from "@/hooks/useExtendedServicePopup";

import styles from "./MerchantInfo.module.css";
import { useExtendedWidgetVisibility } from "@/hooks/useExtendedWidgetVisibility";
import { getButtonText } from "@/utils/extendedViewHelpers";

interface TextGroupProps {
  label: string;
  value?: string | null;
}

const TextGroup = ({ label, value }: TextGroupProps) => {
  if (!value) return null;

  return (
    <div className="text-group">
      <h5>{label}</h5>
      <p>{value}</p>
    </div>
  );
};

interface MerchantInfoProps {
  receipt: Receipt;
  lang: string;
  logoReference?: string | null;
  extended_view_items?: ExtendedViewItem[];
  extended_view_images?: { [key: string]: string };
  user_id?: string;
  receipt_id?: string;
  email?: string | null;
  phoneNumber?: string | null;
}

export const MerchantInfo = ({
  receipt,
  lang,
  logoReference,
  extended_view_items,
  extended_view_images,
  user_id,
  receipt_id,
  email,
  phoneNumber,
}: MerchantInfoProps) => {
  const [logoError, setLogoError] = useState(false);
  const { openExtendedService, isLoading } = useExtendedServicePopup();
  const { merchant } = receipt;
  const { purchase_location } = merchant;

  // Find website item from extended view items
  const { topWidgets } = useExtendedWidgetVisibility({
    extended_view_items,
    extended_view_images,
    currentView: "top_view",
  });

  const handleWebsiteClick = async (widget: ExtendedViewItem) => {
    if (!widget) return;

    // For postmessage version (no user_id/receipt_id), fallback to URL opening
    if (!user_id || !receipt_id || !receipt) {
      if (widget.url) {
        window.open(widget.url, "_blank", "noopener,noreferrer");
      }
      return;
    }

    const receiptEmail = widget.share_url_email && email ? email : null;
    const receiptPhoneNumber =
      widget.share_url_phone_number && phoneNumber ? phoneNumber : null;

    // For website, include all unique articles from the receipt
    const articlesMap = new Map<string, number>();

    receipt.articles?.forEach((art) => {
      if (art.art_number && art.art_final_price !== undefined) {
        const currentTotal = articlesMap.get(art.art_number) ?? 0;
        articlesMap.set(art.art_number, currentTotal + art.art_final_price);
      }
    });

    const articlesPayload = Array.from(articlesMap.entries()).map(
      ([art_number, art_final_price]) => ({ art_number, art_final_price })
    );

    const payload: ExtendedServicePayload = {
      client_service_scope: receipt.merchant.client_id,
      receipt_view_service_settings_id: widget.receipt_view_service_settings_id,
      initiation_language_code: lang,
      receipt_url_email: receiptEmail,
      receipt_url_phone_number: receiptPhoneNumber,
      articles: articlesPayload,
      service_view_position: true,
    };

    await openExtendedService(user_id, receipt_id, payload);
  };

  const formattedAddress = [
    purchase_location?.address,
    purchase_location?.zip_code,
    purchase_location?.city,
    purchase_location?.country_code,
  ]
    .filter(Boolean)
    .join(", ");

  const receiptTypeText = getReceiptTypeDisplayText(receipt.receipt_type);

  const fields = [
    {
      label: translate(lang, "Organisasjonsnr", "Organization nr"),
      value: merchant.org_number,
    },
    {
      label: translate(lang, "Kvitteringsnr", "Receipt nr"),
      value: receipt.receipt_number,
    },
    {
      label: translate(lang, "Adresse", "Address"),
      value: formattedAddress,
    },
    {
      label: translate(lang, "Dato", "Date"),
      value: formatDate(receipt.timestamp, merchant.country_code, false),
    },
    {
      label: translate(lang, "Epost", "Email"),
      value: merchant.email,
    },
    {
      label: translate(lang, "Telefon", "Phone"),
      value: merchant.phone_number,
    },
    {
      label: translate(lang, "Kasse-ID", "Cashier ID"),
      value: purchase_location?.cashier_number,
    },
    {
      label: translate(lang, "Sjåfør-ID", "Driver ID"),
      value: receipt.merchant.travel_reference?.driver_id,
    },
    {
      label: translate(lang, "Navn på sjåfør", "Name of driver"),
      value: receipt.merchant.travel_reference?.driver_name,
    },
    {
      label: translate(lang, "Kjøretøysertifiseringsnr", "Vehicle cert. nr"),
      value: receipt.merchant.travel_reference?.driver_certification,
    },
    {
      label: translate(lang, "Reisesertifiseringsnr", "Travel cert. nr"),
      value: receipt.merchant.travel_reference?.company_certification,
    },
    {
      label: translate(lang, "Registreringsnummer", "License plate"),
      value: receipt.merchant.travel_reference?.license_plate,
    },
  ].filter((field): field is { label: string; value: string } => !!field.value);

  const merchantInfoRows = [];
  for (let i = 0; i < fields.length; i += 2) {
    merchantInfoRows.push(
      <div className="flex" key={i}>
        {fields.slice(i, i + 2).map((field) => (
          <TextGroup key={field.label} label={field.label} value={field.value} />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="box head">
        <div className={styles.merchantHeader}>
          {logoReference !== undefined && (
            <div className={styles.logoContainer}>
              <Image
                src={logoError ? logo : (logoReference ?? logo)}
                fill
                className={styles.logoImage}
                alt="Merchant logo"
                onError={() => setLogoError(true)}
              />
            </div>
          )}
          <h2 className={styles.storeName}>
            {purchase_location?.store_name ||
              translate(
                lang,
                `Kvittering: ${receipt.receipt_number}`,
                `Receipt: ${receipt.receipt_number}`
              )}
          </h2>
          {topWidgets?.map((widget) => {
            const itemKey = `top_${widget.originalIndex}`;
            return (
              <Button
                key={itemKey}
                className={styles.websiteButton}
                onClick={() => handleWebsiteClick(widget)}
                text={getButtonText(widget.extended_view_position, lang, {
                  receipt_button_text: widget.receipt_button_text,
                })}
                isLoading={isLoading()}
                icon={
                  widget.imageSrc ? (
                    <Image
                      src={widget.imageSrc}
                      alt="Website"
                      width={16}
                      height={16}
                      className={styles.websiteIcon}
                    />
                  ) : undefined
                }
              />
            );
          })}
        </div>
      </div>

      {receiptTypeText && (
        <div className="box">
          <p className={styles.receiptType}>{receiptTypeText}</p>
        </div>
      )}

      <div className="box">
        {merchantInfoRows}
      </div>
    </>
  );
};
